import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import { InfoTooltip } from '@sales-center/components'
import * as React from 'react'
import { useCallback, useState } from 'react'
import { getUserWithCounts, ITenantUser } from '@user/store/users'
import useGetUserStatusToggleMsg from '@user/components/Settings/Users/hooks/useGetUserStatusToggleMsg'
import { ReAssignRecords } from '@user/components/Settings/Users/ReAssignRecords/ReAssignRecords'
import {
  activeUserRecords,
  adminRoleName,
  guestRoleName,
} from '@user/components/Settings/Users/UpdateUser/constants'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { userHasOpenRecords } from './functions'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'

interface Props {
  user: ITenantUser
  updateUserStatus?: (user: ITenantUser, reloadUsers?: boolean) => void
  handleEditClick?: (userId: string, initialActive?: boolean) => void
}

export function UserStatusToggle(props: Props) {
  const { user, updateUserStatus, handleEditClick } = props
  const { id, isActive, emailIsUnique } = user

  const [reAssignDrawerOpen, setReAssignDrawerOpen] = useState(false)

  const currentUser = useSelector(selectCurrentUser)

  const activeMessage = useGetUserStatusToggleMsg(user, emailIsUnique)
  const dispatch = useAppDispatch()

  const onChangeStatus = useCallback(async () => {
    const tenantUser = await dispatch(getUserWithCounts(user.id)).unwrap()

    const { isActive, roleId, roleName, salesTeamPositionIds } = tenantUser
    const hasOpenRecords = userHasOpenRecords(tenantUser)

    if (hasOpenRecords) {
      setReAssignDrawerOpen(true)
    } else {
      if (
        handleEditClick &&
        !isActive &&
        (!roleId ||
          (salesTeamPositionIds?.length == 0 &&
            roleName != adminRoleName &&
            roleName != guestRoleName))
      ) {
        handleEditClick(id, true)
      } else {
        if (updateUserStatus) updateUserStatus({ ...user, isActive: !isActive })
      }
    }
  }, [setReAssignDrawerOpen, handleEditClick, dispatch, updateUserStatus])

  const onReAssignSuccess = useCallback(() => {
    if (updateUserStatus)
      updateUserStatus({ ...user, isActive: !isActive }, true)
  }, [isActive, updateUserStatus, user])

  return (
    <>
      <FormControlLabel
        sx={{
          marginBottom: 0,
          gap: (theme) => theme.spacing(0.8),
        }}
        labelPlacement="end"
        label={isActive ? 'Active' : 'Inactive'}
        control={
          <Switch
            size={'small'}
            disabled={
              currentUser.userId === user.id || (activeMessage ? true : false)
            }
            checked={isActive}
            onChange={onChangeStatus}
          />
        }
      />

      {activeMessage && activeMessage !== activeUserRecords && (
        <InfoTooltip
          infoText={activeMessage}
          tooltipPlacement="right"
          sx={{ fontSize: '18px' }}
          iconColor="action"
        />
      )}

      {reAssignDrawerOpen && (
        <ReAssignRecords
          open={reAssignDrawerOpen}
          onClose={setReAssignDrawerOpen}
          onReAssignSuccess={onReAssignSuccess}
          userId={user.id}
        />
      )}
    </>
  )
}
